/**
* @author Paulius Ambroza, Algirdas Noreika, Indeform Ltd
* @copyright Copyright 2018, Indeform Ltd. DO NOT EDIT/USE WITHOUT PERMISSION.
* @version 1.6
* @description Native javascript REST API handler using ES6 standarts.
* Usage:
*   let rest = new Rest(baseUrl);
*   net.{TYPE}({PROPERTIES}).then({SUCESSCALLBACK}).catch({FAILURECALLBACK});
*
*   TYPE - get/post/delete/put
*   PROPERTIES - {
*                   url: REST api url
*                   data: data to be sent (can be null or undefined)
*                   acceptedStatuses: only accept defined response statuses. defaut - []
*                   isJson: to convert data to json or not. default - true,
*                   headers: array of header objects {key: string, value: string}
*                   onProgress: function to manage progress display,
*                   serialize: to convert data to URL encoded string or not. default - false
*                }
*   SUCCESSCALLBACK - function to process response. Response data as single argument.
*   FAILURECALLBACK - function to handle errors. Javascript Object as a single argument.
*       Object contains "errMsg", "errType", "errResponse" properties
*
*   net.url - utility method to construct urls if base url given. Define baseUrl in constructor.
*   net.setBaseUrl - modify base url
*
*   EXAMPLE:
*
*   let rest = new Rest("https://example.com/api/");
*   net.get({url: net.url("cats"), data: null}).then((data) => displayCats(data)).catch((e) => console.log(e));
*
*   errType constants:
*       Net.ERR_NETWORK      Request failed (No connection, wrong URL)
*       Net.ERR_STATUS       Response status is above 400 or not in allowed response status list
*       Net.ERR_RESPONSE     Response did not match expected response type (eg. Expected JSON string, received HTML)
*       Net.ERR_ABORT        Request has been aborted
*
*   1.6 - Added data URL encoding
*   1.5 - Code optimizations (ALN)
*   1.4 - Added onProgress functionality
*   1.3 - Added rejects for empty responses
*   1.2 - Added custom headers
*   1.1 - Promise rejects a custom object
*   1.0 - Initial setup
*/


class Net {
    /**
     * Constructor
     * @param {String} [baseUrl='']  Optional parameter to set base url. Prepends to all API calls if preset
     */
    constructor(core, baseUrl = '') {
        this.core = core;
        this.baseUrl = baseUrl;
    }


    /**
     * Generates API call url
     * @param  {String} url Endpoint URL
     * @return {String}     Returns URL to endpoint prepending base url if set
     */
    url(url) {
        return this.baseUrl + url;
    }


    // shortcuts to specific type calls: POST/DELETE/PUT/GET
    post(params) {
        return this.ajax("POST", params.url, params.data, params.acceptedStatuses, params.isJson, params.headers, params.onProgress, params.serialize, params.isAsynchronous);
    }

    get(params) {
        return this.ajax("GET", params.url, params.data, params.acceptedStatuses, params.isJson, params.headers, params.onProgress, params.serialize, params.isAsynchronous);
    }

    put(params) {
        return this.ajax("PUT", params.url, params.data, params.acceptedStatuses, params.isJson, params.headers, params.onProgress, params.serialize, params.isAsynchronous);
    }

    delete(params) {
        return this.ajax("DELETE", params.url, params.data, params.acceptedStatuses, params.isJson, params.headers, params.onProgress, params.serialize, params.isAsynchronous);
    }


    /**
     * XMLHttpRequest to backend to retrieve data
     * @param  {String}     type                  GET/POST/PUT/DELETE
     * @param  {String}     url                   Endpoint URL
     * @param  {Object}     [data=null]           Data to send to endpoint
     * @param  {Array}      [acceptedStatuses=[]] Allow only specific response codes
     * @param  {Boolean}    [isJson=true]         JSON data and contentype conversion
     * @param  {Array}      [headers=[]]          Array of custom header values
     * @param  {Function}   [onProgress=null]     onProgress event handler function
     * @param  {Boolean}    [serialize=false]     URL encoded data and contentype conversion
     * @return {Promise}                          Returns JS ES6 Promise
     */
    ajax(type, url, data = null, acceptedStatuses = [], isJson = true, headers = [], onProgress = null, serialize = false, isAsynchronous = true) {
        let contentType = isJson === true ? "application/json; charset=utf-8" : (serialize === true ? "application/x-www-form-urlencoded; charset=UTF-8" : false);
        let ajaxData = this.prepareData(data, isJson, serialize);

        return new Promise((resolve, reject) =>{
            const xhr = new XMLHttpRequest();
            xhr.open(type, this.baseUrl + url, isAsynchronous);

            if (headers.length > 0){
                headers.forEach((header) => {
                    xhr.setRequestHeader(header.key, header.value);
                });
            }

            if (contentType) xhr.setRequestHeader("Content-type", contentType);

            xhr.onload = () => {
                if (xhr.status >= 400){
                    reject(this.getErrorObject(
                        `Response Status Error: ${xhr.status} ${xhr.statusText}`,
                        Net.ERR_STATUS,
                        xhr.response
                    ));
                }

                if (acceptedStatuses.length > 0){
                    if (acceptedStatuses.indexOf(xhr.status) === -1){
                        reject(this.getErrorObject(
                            `Response Status Error: Status code ${xhr.status} is not in allowed response status list.`,
                            Net.ERR_STATUS,
                            xhr.response
                        ));
                    }
                }

                if (isJson === true){
                    if (xhr.responseText === ""){
                        reject(this.getErrorObject(
                            `Response Error: response is null`,
                            Net.ERR_RESPONSE,
                            xhr.response
                        ));
                    }
                    try {
                        let responseJson = JSON.parse(xhr.responseText);
                        if (responseJson === null){
                            reject(this.getErrorObject(
                                `Response Error: response is null`,
                                Net.ERR_RESPONSE,
                                xhr.response
                            ));
                        }
                        resolve(responseJson);
                    } catch (e) {
                        reject(this.getErrorObject(
                            `Response Error: ${e}`,
                            Net.ERR_RESPONSE,
                            xhr.response
                        ));
                    }
                } else {
                    resolve(xhr.response);
                }
            }

            if (onProgress !== null){
                xhr.upload.onprogress = (e) => {
                    onProgress({
                        "loaded": e.loaded,
                        "total": e.total,
                        "timestamp": e.timeStamp,
                        "progress": Math.round((e.loaded / e.total) * 100),
                        "xhr": xhr
                    });
                };
            }

            xhr.onabort = (e) => reject(this.getErrorObject(
                `Request aborted ${e}`,
                Net.ERR_ABORT,
                null
            ));
            xhr.onerror = (e) => reject(this.getErrorObject(
                `Network Error: ${e}`,
                Net.ERR_NETWORK,
                null
            ));
            xhr.send(ajaxData);
        });
    }


    /**
     * Prepare data for ajax request
     * @param  {Object}  data       Data to send
     * @param  {Boolean} isJson     To JSON stringify data
     * @param  {Boolean} serialize  To url encode data
     * @return {*}                  JSON stringified data, FormData/JS Object or serialized URL data
     */
    prepareData(data, isJson, serialize) {
        if (isJson === true){
            return this.isDataJson(data) ? data : JSON.stringify(data);
        } else {
            return serialize ? this.serialize(data) : data;
        }
    }


    /**
     * URL encode data
     * @param   {Object}  data  Data to serialize. {key: value}
     * @return  {String}        Serialized URL data
     */
    serialize(data) {
        let str = [];
        for(let key in data) {
            str.push(key + "=" + data[key]);
        }
        return str.join("&");
    }


    /**
     * Check if incoming data is JSON stringified
     * @param  {*}       s Incoming data to check
     * @return {Boolean}   Valid JSON string
     */
    isDataJson(s) {
        try {
            JSON.parse(s);
        } catch (e) {
            return false;
        }
        return true;
    }


    /**
     * Modifies base url property
     * @param {String} url String parameter to replace base url value
     */
    setBaseUrl(url){
        this.baseUrl = url;
    }


    /**
     * Generates error object for Promise reject
     * @param  {String}  msg      Readable error message
     * @param  {Integer} type     Error code
     * @param  {*}       response XMLHttpRequest response data
     * @return {Object}           Returns error object
     */
    getErrorObject(msg, type, response) {
        return {
            errMsg: msg,
            errType: type,
            errResponse: response
        };
    }


    // Static error codes
    static get ERR_NETWORK(){ return 12001; }
    static get ERR_STATUS(){ return 12002; }
    static get ERR_RESPONSE(){ return 12003; }
    static get ERR_ABORT(){ return 12004; }
}



export { Net }
export default Net
